'use client';

import React, { useState } from 'react';
import Link from 'next/link';

// ─── TYPES ────────────────────────────────────────────────────────────────────

interface RoleArchetype {
  id: string;
  label: string;
  arabic: string;
  tagline: string;
  color: string;
  icon: string;
  biome: string;
  biomeColor: string;
  // Branching flow data
  firstSurface: string; // What they see first
  keys: { primary: string; secondary: string; note: string };
  sequence: { name: string; entry: string; prompt: string };
  mrgDomains: { domain: string; trust: string; action: string }[];
  amanahBridge: string;
  irlEntry: string;
  // Vocational IRL re-orientation
  vocationalLens: {
    question: string;
    observation: string;
    namesActive: string[];
    fieldPrompt: string;
  };
  // Publish/remix
  publishForm: { title: string; fields: string[] };
}

// ─── ROLE ARCHETYPES DATA ─────────────────────────────────────────────────────

const ROLE_ARCHETYPES: RoleArchetype[] = [
  {
    id: 'engineer',
    label: 'Engineer',
    arabic: 'مُهَنْدِس',
    tagline: 'Systems, provision, and the architecture of creation',
    color: '#2d8a87',
    icon: '⚙',
    biome: 'Forest Watershed',
    biomeColor: '#2d7a47',
    firstSurface: 'Systems & Provision — the engineer sees infrastructure first',
    keys: {
      primary: 'Key 02: Haal — States of Being as system states',
      secondary: 'Key 04: Mizan — The Balance as engineering constraint',
      note: 'Engineers recognize Sunnat Allah as the operating system of creation — the laws that govern every system they build within.',
    },
    sequence: {
      name: 'Mycorrhizal Network (S4)',
      entry: 'The hidden infrastructure beneath the forest floor',
      prompt: 'What tools is an engineer working on, and what tools does this engineer utilize toward that purpose?',
    },
    mrgDomains: [
      { domain: 'Infrastructure & Housing', trust: 'Shelter is Amanah — not property', action: 'Map structural gaps in your ward' },
      { domain: 'Environmental Systems', trust: 'Water, soil, air are trusts', action: 'Document one system imbalance' },
      { domain: 'Technology & Tools', trust: 'Tools are Mata — provisions, not ends', action: 'Identify one tool serving Islah' },
    ],
    amanahBridge: 'The engineer\'s Amanah: every system built either upholds or transgresses the Mizan. What does your current work maintain?',
    irlEntry: '/inner-reality-lens',
    vocationalLens: {
      question: 'What tools is an engineer working on, and what tools does this engineer utilize toward that purpose?',
      observation: 'Observe one engineered system in your locality — a bridge, a water treatment plant, a power grid. Apply the Three Questions: Is it? What is it? When is it?',
      namesActive: ['Al-Qadir (The All-Powerful — the original engineer)', 'Al-Musawwir (The Fashioner — form follows function)', 'Al-Latif (The Subtle — hidden systems)'],
      fieldPrompt: 'What measurement (Taqdir) was given to this system? What Name of Allah is most evident in its design?',
    },
    publishForm: {
      title: 'Engineer Lens Module',
      fields: ['System observed', 'Names extracted', 'Mizan assessment', 'Islah proposal', 'Community impact'],
    },
  },
  {
    id: 'teacher',
    label: 'Teacher',
    arabic: 'مُعَلِّم',
    tagline: 'Curriculum, transmission, and the Ilm that flows',
    color: '#8a8ab8',
    icon: '✦',
    biome: 'Canopy Photosynthesis',
    biomeColor: '#2d7a47',
    firstSurface: 'Curriculum & Knowledge — the teacher sees transmission first',
    keys: {
      primary: 'Key 01: Ayat — Signs as the curriculum of creation',
      secondary: 'Key 07: Yaqeen — Three levels of certainty as pedagogical stages',
      note: 'Teachers recognize that the best curriculum is creation itself — Ilm Yaqeen (read about it), Ayn Yaqeen (see it), Haqq Yaqeen (experience it directly).',
    },
    sequence: {
      name: 'Canopy Photosynthesis (S3)',
      entry: 'Light as the first teacher — energy flowing through the system',
      prompt: 'What does the teacher\'s curriculum look like? How does light teach the leaf?',
    },
    mrgDomains: [
      { domain: 'Education & Literacy', trust: 'Knowledge is Amanah — to be transmitted, not hoarded', action: 'Map educational gaps in your ward' },
      { domain: 'Youth Development', trust: 'The next generation is a trust', action: 'Identify one mentorship opportunity' },
      { domain: 'Community Learning', trust: 'Informal education is Ilm too', action: 'Document one community knowledge hub' },
    ],
    amanahBridge: 'The teacher\'s Amanah: what you transmit shapes what is recognized. What curriculum are you offering — and does it point toward the One Who taught?',
    irlEntry: '/inner-reality-lens',
    vocationalLens: {
      question: 'What does the teacher\'s curriculum look like? What does the canopy teach the understory?',
      observation: 'Observe one teaching moment in your locality — a classroom, a masjid circle, a parent explaining something to a child. Apply the Three Questions.',
      namesActive: ['Al-Alim (The All-Knowing — the source of all Ilm)', 'Al-Hakim (The All-Wise — wisdom in transmission)', 'Al-Hadi (The Guide — comprehensive guidance)'],
      fieldPrompt: 'What level of Yaqeen (Ilm/Ayn/Haqq) does this teaching moment reach? What would move it deeper?',
    },
    publishForm: {
      title: 'Teacher Lens Module',
      fields: ['Teaching context observed', 'Yaqeen level reached', 'Names in the curriculum', 'Ilm Dock entry', 'Community transmission proposal'],
    },
  },
  {
    id: 'scientist',
    label: 'Scientist',
    arabic: 'عَالِم',
    tagline: 'Research, discovery, and the Sunnat Allah made visible',
    color: '#c9a84c',
    icon: '◎',
    biome: 'Wetland Filtration',
    biomeColor: '#2d8a87',
    firstSurface: 'Research & Discovery — the scientist sees patterns first',
    keys: {
      primary: 'Key 01: Ayat — Signs as data points in creation',
      secondary: 'Key 02: Haal — States of Being as observable phenomena',
      note: 'Scientists recognize that every discovery is an uncovering of what was already decreed — the Taqdir of each thing, measured and written before the pen was lifted.',
    },
    sequence: {
      name: 'Wetland Filtration (S2)',
      entry: 'The chemistry of purification — Al-Latif in the filtration process',
      prompt: 'What breakthrough research motivates the scientist? What Sunnat Allah is being uncovered?',
    },
    mrgDomains: [
      { domain: 'Environmental Health', trust: 'Clean water and air are Amanah', action: 'Document one environmental health gap' },
      { domain: 'Research & Data', trust: 'Data is Ilm — to be shared, not siloed', action: 'Map one community data gap' },
      { domain: 'Public Health', trust: 'Health is a trust — community-wide', action: 'Identify one preventable health disparity' },
    ],
    amanahBridge: 'The scientist\'s Amanah: every discovery is a revelation of what Allah already decreed. What does your research reveal about the Taqdir of creation?',
    irlEntry: '/inner-reality-lens',
    vocationalLens: {
      question: 'What breakthrough research motivates the scientist? What Sunnat Allah is being uncovered in the lab?',
      observation: 'Observe one natural process in your locality — filtration, decomposition, growth, migration. Document it through the 8-layer IRL pathway.',
      namesActive: ['Al-Khabir (The All-Aware — intrinsic knowledge)', 'Al-Latif (The Subtle — hidden processes)', 'Al-Badi (The Originator — first causes)'],
      fieldPrompt: 'What measurement (Taqdir) was given to this process? What would it mean to conduct this research Fisabilillah?',
    },
    publishForm: {
      title: 'Scientist Lens Module',
      fields: ['Process observed', 'Sunnat Allah identified', 'Names extracted', 'Research question', 'Community application'],
    },
  },
  {
    id: 'farmer',
    label: 'Farmer',
    arabic: 'فَلَّاح',
    tagline: 'Soil, growth, and the Falah that feeds',
    color: '#2d7a47',
    icon: '⬡',
    biome: 'Big Bluestem Prairie',
    biomeColor: '#c9a84c',
    firstSurface: 'Soil & Growth — the farmer sees the ground first',
    keys: {
      primary: 'Key 06: Falah — Flourishing as cultivation, not accumulation',
      secondary: 'Key 05: Amanah — The soil is a trust, not a resource',
      note: 'Farmers recognize that they plant but do not cause to grow — "not a tree of theirs was it for you to cause to grow" (Quran 27:60). The growing is Allah\'s.',
    },
    sequence: {
      name: 'Big Bluestem Prairie (S1)',
      entry: 'Deep roots in shallow soil — the prairie\'s hidden infrastructure',
      prompt: 'Which kinds of plants is the gardener planting? Where, how, why, and what Ayat is that gardener pondering while doing so?',
    },
    mrgDomains: [
      { domain: 'Food Security', trust: 'Food is Razzaq — provision from Al-Razzaq', action: 'Map food deserts in your ward' },
      { domain: 'Land & Agriculture', trust: 'Land is Amanah — not property', action: 'Identify one community garden opportunity' },
      { domain: 'Environmental Stewardship', trust: 'Soil health is a generational trust', action: 'Document one soil degradation site' },
    ],
    amanahBridge: 'The farmer\'s Amanah: you plant, He grows. The harvest is not yours — it is provision from Ar-Razzaq flowing through your hands. What are you planting?',
    irlEntry: '/inner-reality-lens',
    vocationalLens: {
      question: 'Which kinds of plants is the gardener planting? What Ayat is that gardener pondering while doing so?',
      observation: 'Observe one growing thing in your locality — a community garden, a street tree, a vacant lot with volunteer plants. Apply the Three Questions.',
      namesActive: ['Ar-Razzaq (The Provider — all provision flows from Him)', 'Al-Muhyi (The Bestower of Life)', 'Al-Khaliq (The Creator — the growing is His)'],
      fieldPrompt: 'Quran 27:60: "not a tree of theirs was it for you to cause to grow." What does this mean for your work in the soil?',
    },
    publishForm: {
      title: 'Farmer Lens Module',
      fields: ['Plant/crop observed', 'Soil condition', 'Names in the growth', 'Quran 27:60 reflection', 'Community food bridge'],
    },
  },
  {
    id: 'native',
    label: 'Native',
    arabic: 'أَصِيل',
    tagline: 'Place, belonging, and the Mizan of the land',
    color: '#c4956a',
    icon: '◈',
    biome: 'Longleaf Pine Savannah',
    biomeColor: '#c9a84c',
    firstSurface: 'Place & Belonging — the native sees the land first',
    keys: {
      primary: 'Key 04: Mizan — The Balance of the land as lived knowledge',
      secondary: 'Key 05: Amanah — Stewardship as the original relationship to place',
      note: 'Natives recognize that the land was never owned — it was always held in trust. Their ethical decisions about sustainable urban planning are rooted in this recognition.',
    },
    sequence: {
      name: 'Longleaf Pine Savannah (S7)',
      entry: 'A 100-year tree — generational Falah in the landscape',
      prompt: 'What value propositions are directing the native\'s ethical decisions regarding sustainable urban planning in their locality?',
    },
    mrgDomains: [
      { domain: 'Land Rights & Sovereignty', trust: 'The land is Amanah — not commodity', action: 'Map land use patterns in your locality' },
      { domain: 'Cultural Preservation', trust: 'Indigenous knowledge is Ilm', action: 'Document one traditional ecological practice' },
      { domain: 'Environmental Justice', trust: 'Clean land is a right, not a privilege', action: 'Identify one environmental injustice in your ward' },
    ],
    amanahBridge: 'The native\'s Amanah: you were appointed to this station. The land you stand on is your test. What does sustainable urban planning look like when the land is held as Amanah?',
    irlEntry: '/inner-reality-lens',
    vocationalLens: {
      question: 'What value propositions are directing the native\'s ethical decisions regarding sustainable urban planning in their locality?',
      observation: 'Observe one place in your locality that has been transformed — a park, a neighborhood, a watershed. What was it? What is it now? What should it be?',
      namesActive: ['Al-Wali (The Protector — stewardship)', 'Al-Hafiz (The Preserver — what endures)', 'Al-Adl (The Just — land justice)'],
      fieldPrompt: 'What does decentralized resource management look like in your locality? What Mizan is being maintained or transgressed?',
    },
    publishForm: {
      title: 'Native Lens Module',
      fields: ['Place observed', 'Historical context', 'Current Mizan assessment', 'Traditional knowledge', 'Sustainable planning proposal'],
    },
  },
  {
    id: 'returning-citizen',
    label: 'Returning Citizen',
    arabic: 'عَائِد',
    tagline: 'Mizan, restoration, and the Islah that heals',
    color: '#5a5a8a',
    icon: '⌘',
    biome: 'Salmon Run',
    biomeColor: '#2d8a87',
    firstSurface: 'Mizan & Restoration — the returning citizen sees what needs healing first',
    keys: {
      primary: 'Key 04: Mizan — Restoration as the work of re-establishing balance',
      secondary: 'Key 05: Amanah — The community is a trust to be returned to',
      note: 'Returning citizens recognize Islah — to cultivate, mend, restore, renew — as the precise word for their work. The complete fix is rooted in Salah, which shares the same root.',
    },
    sequence: {
      name: 'Salmon Run (S6)',
      entry: 'Return, restoration, and the upstream journey',
      prompt: 'What reforms and resource redistribution propositions does someone formerly incarcerated have for existing community institutions, and what are the core tenets of their reasoning?',
    },
    mrgDomains: [
      { domain: 'Reentry Support', trust: 'Reintegration is a community Amanah', action: 'Map reentry resources in your ward' },
      { domain: 'Justice Reform', trust: 'Justice is Mizan — not punishment', action: 'Document one systemic injustice' },
      { domain: 'Mutual Aid', trust: 'Community care is Khidmah', action: 'Connect to one mutual aid network' },
    ],
    amanahBridge: 'The returning citizen\'s Amanah: you were appointed to this station. The community you return to is your test. What Islah does your experience uniquely equip you to offer?',
    irlEntry: '/inner-reality-lens',
    vocationalLens: {
      question: 'What reforms and resource redistribution propositions does someone formerly incarcerated have for existing community institutions?',
      observation: 'Observe one community institution in your locality — a school, a clinic, a masjid, a community center. Apply the Three Questions: Is it serving its Amanah? What is its essential nature? When did it begin to drift?',
      namesActive: ['Al-Tawwab (The Acceptor of Repentance — restoration)', 'Al-Jabbar (The Restorer — the One who sets right)', 'Al-Adl (The Just — systemic justice)'],
      fieldPrompt: 'The Salmon Run: the salmon returns upstream to restore the ecosystem. What upstream work does your return enable?',
    },
    publishForm: {
      title: 'Returning Citizen Lens Module',
      fields: ['Institution observed', 'Islah assessment', 'Reform proposal', 'Core tenets', 'Community bridge'],
    },
  },
];

// ─── BRANCHING ONBOARDING FLOW ────────────────────────────────────────────────

interface BranchingOnboardingFlowProps {
  roleId: 'engineer' | 'farmer' | 'returning-citizen';
}

export function BranchingOnboardingFlow({ roleId }: BranchingOnboardingFlowProps) {
  const role = ROLE_ARCHETYPES.find(r => r.id === roleId);
  const [step, setStep] = useState(0);

  if (!role) return null;

  const steps = [
    { id: 'surface', label: 'First Surface', icon: '◎' },
    { id: 'keys', label: 'Keys', icon: '⌘' },
    { id: 'sequence', label: 'Sequence', icon: '✦' },
    { id: 'mrg', label: 'MRG Domains', icon: '⬡' },
    { id: 'amanah', label: 'Amanah Bridge', icon: '◈' },
  ];

  return (
    <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${role.color}30` }}>
      {/* Header */}
      <div className="p-6" style={{ background: `${role.color}10`, borderBottom: `1px solid ${role.color}20` }}>
        <div className="flex items-center gap-4">
          <span className="text-3xl" style={{ color: role.color }}>{role.icon}</span>
          <div>
            <h3 className="font-mono text-xl font-black uppercase tracking-tight" style={{ color: '#F5F0E8' }}>{role.label}</h3>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 16, color: `${role.color}70`, direction: 'rtl' }}>{role.arabic}</p>
          </div>
          <div className="ml-auto text-right">
            <p className="font-mono text-[9px] uppercase tracking-widest" style={{ color: `${role.color}60` }}>Biome</p>
            <p className="font-mono text-[10px] font-black" style={{ color: role.biomeColor }}>{role.biome}</p>
          </div>
        </div>
        <p className="font-mono text-[10px] italic mt-3" style={{ color: 'rgba(212,197,169,0.5)' }}>{role.tagline}</p>
      </div>

      {/* Step nav */}
      <div className="flex overflow-x-auto" style={{ borderBottom: `1px solid ${role.color}15` }}>
        {steps.map((s, idx) => (
          <button
            key={s.id}
            onClick={() => setStep(idx)}
            className="flex-shrink-0 flex items-center gap-2 px-4 py-3 font-mono text-[9px] uppercase tracking-widest transition-all"
            style={{
              background: step === idx ? `${role.color}12` : 'transparent',
              borderBottom: step === idx ? `2px solid ${role.color}` : '2px solid transparent',
              color: step === idx ? role.color : 'rgba(212,197,169,0.35)',
            }}
          >
            <span>{s.icon}</span>
            <span className="hidden sm:block">{s.label}</span>
          </button>
        ))}
      </div>

      {/* Step content */}
      <div className="p-6" style={{ background: 'rgba(6,13,18,0.6)' }}>
        {step === 0 && (
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: `${role.color}60` }}>First Surface</p>
            <p className="font-mono text-sm font-black mb-4" style={{ color: '#F5F0E8' }}>{role.firstSurface}</p>
            <div className="rounded-lg p-4" style={{ background: `${role.color}08`, border: `1px solid ${role.color}20` }}>
              <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, lineHeight: 1.7 }}>
                The {role.label} enters the platform through the lens of their vocation. Before the framework is introduced, the platform surfaces what is most immediately legible to this role: <strong style={{ color: role.color }}>{role.firstSurface.split('—')[0].trim()}</strong>.
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: `${role.color}60` }}>Role-Specific Keys</p>
            <div className="space-y-3 mb-4">
              <div className="rounded-lg p-4" style={{ background: `${role.color}08`, border: `1px solid ${role.color}20` }}>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: `${role.color}60` }}>Primary Key</p>
                <p className="font-mono text-sm font-black" style={{ color: '#F5F0E8' }}>{role.keys.primary}</p>
              </div>
              <div className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="font-mono text-[9px] uppercase tracking-widest mb-1" style={{ color: 'rgba(212,197,169,0.3)' }}>Secondary Key</p>
                <p className="font-mono text-sm font-black" style={{ color: '#F5F0E8' }}>{role.keys.secondary}</p>
              </div>
            </div>
            <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>{role.keys.note}</p>
          </div>
        )}

        {step === 2 && (
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: `${role.color}60` }}>Entry Sequence</p>
            <div className="rounded-lg p-5 mb-4" style={{ background: `${role.biomeColor}08`, border: `1px solid ${role.biomeColor}25` }}>
              <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: `${role.biomeColor}60` }}>Sequence</p>
              <p className="font-mono text-sm font-black mb-2" style={{ color: '#F5F0E8' }}>{role.sequence.name}</p>
              <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12, marginBottom: 12 }}>{role.sequence.entry}</p>
              <div className="rounded p-3" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                <p className="font-mono text-[8px] uppercase tracking-widest mb-1" style={{ color: 'rgba(201,168,76,0.5)' }}>Role Prompt</p>
                <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12, fontStyle: 'italic' }}>{role.sequence.prompt}</p>
              </div>
            </div>
            <Link href={role.irlEntry} className="inline-block font-mono text-[10px] tracking-widest uppercase px-4 py-2 transition-all" style={{ background: `${role.color}15`, color: role.color, border: `1px solid ${role.color}30`, textDecoration: 'none' }}>
              Enter IRL Lens →
            </Link>
          </div>
        )}

        {step === 3 && (
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: `${role.color}60` }}>MRG Domains</p>
            <div className="space-y-3">
              {role.mrgDomains.map((d, i) => (
                <div key={i} className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="font-mono text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: role.color }}>{d.domain}</p>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 11, marginBottom: 6, fontStyle: 'italic' }}>{d.trust}</p>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12 }}>→ {d.action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: `${role.color}60` }}>Amanah Bridge</p>
            <div className="rounded-xl p-6" style={{ background: `${role.color}08`, border: `1px solid ${role.color}25` }}>
              <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 14, lineHeight: 1.85, fontStyle: 'italic' }}>
                "{role.amanahBridge}"
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between p-4" style={{ borderTop: `1px solid ${role.color}15` }}>
        <button
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 transition-all"
          style={{ color: step === 0 ? 'rgba(212,197,169,0.2)' : 'rgba(212,197,169,0.5)', border: `1px solid ${step === 0 ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.1)'}` }}
        >
          ← Back
        </button>
        <span className="font-mono text-[9px]" style={{ color: 'rgba(212,197,169,0.3)' }}>{step + 1} / {steps.length}</span>
        <button
          onClick={() => setStep(Math.min(steps.length - 1, step + 1))}
          disabled={step === steps.length - 1}
          className="font-mono text-[9px] uppercase tracking-widest px-4 py-2 transition-all"
          style={{ color: step === steps.length - 1 ? 'rgba(212,197,169,0.2)' : role.color, border: `1px solid ${step === steps.length - 1 ? 'rgba(255,255,255,0.04)' : role.color + '30'}` }}
        >
          Next →
        </button>
      </div>
    </div>
  );
}

// ─── SIX SEED ROLE MODULE GRID ────────────────────────────────────────────────

export function SixSeedRoleModuleGrid() {
  const [activeRole, setActiveRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'lens' | 'amanah' | 'publish'>('lens');
  const [publishForm, setPublishForm] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);

  const role = ROLE_ARCHETYPES.find(r => r.id === activeRole);

  const handlePublishSubmit = () => {
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setPublishForm({});
  };

  return (
    <div>
      {/* Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        {ROLE_ARCHETYPES.map(r => (
          <button
            key={r.id}
            onClick={() => { setActiveRole(activeRole === r.id ? null : r.id); setActiveTab('lens'); setSubmitted(false); }}
            className="text-left p-4 rounded-xl transition-all duration-300"
            style={{
              background: activeRole === r.id ? `${r.color}12` : 'rgba(255,255,255,0.02)',
              border: `1px solid ${activeRole === r.id ? r.color + '50' : 'rgba(255,255,255,0.06)'}`,
              borderLeft: `3px solid ${activeRole === r.id ? r.color : 'transparent'}`,
            }}
          >
            <span className="text-2xl block mb-2" style={{ color: r.color }}>{r.icon}</span>
            <p className="font-mono text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: '#F5F0E8' }}>{r.label}</p>
            <p style={{ fontFamily: "'Amiri', serif", fontSize: 13, color: `${r.color}60`, direction: 'rtl' }}>{r.arabic}</p>
            <p className="font-mono text-[8px] mt-2" style={{ color: 'rgba(212,197,169,0.35)' }}>{r.biome}</p>
          </button>
        ))}
      </div>

      {/* Expanded role panel */}
      {role && (
        <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${role.color}30` }}>
          {/* Tab nav */}
          <div className="flex" style={{ borderBottom: `1px solid ${role.color}15` }}>
            {[
              { id: 'lens', label: 'Vocational IRL Lens' },
              { id: 'amanah', label: 'Amanah Domains' },
              { id: 'publish', label: 'Publish / Remix' },
            ].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id as typeof activeTab)}
                className="flex-1 py-3 font-mono text-[9px] uppercase tracking-widest transition-all"
                style={{
                  background: activeTab === t.id ? `${role.color}10` : 'transparent',
                  borderBottom: activeTab === t.id ? `2px solid ${role.color}` : '2px solid transparent',
                  color: activeTab === t.id ? role.color : 'rgba(212,197,169,0.35)',
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="p-6" style={{ background: 'rgba(6,13,18,0.7)' }}>
            {activeTab === 'lens' && (
              <div className="space-y-4">
                <div className="rounded-lg p-4" style={{ background: `${role.color}08`, border: `1px solid ${role.color}20` }}>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: `${role.color}60` }}>Vocational Question</p>
                  <p style={{ color: 'rgba(212,197,169,0.8)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>"{role.vocationalLens.question}"</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.3)' }}>Field Observation</p>
                  <p style={{ color: 'rgba(212,197,169,0.65)', fontSize: 13, lineHeight: 1.7 }}>{role.vocationalLens.observation}</p>
                </div>
                <div>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(212,197,169,0.3)' }}>Names Active</p>
                  <div className="space-y-1">
                    {role.vocationalLens.namesActive.map(n => (
                      <div key={n} className="flex items-start gap-2">
                        <span style={{ color: role.color }}>✦</span>
                        <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12 }}>{n}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="rounded-lg p-4" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: 'rgba(201,168,76,0.5)' }}>Field Prompt</p>
                  <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 13, fontStyle: 'italic' }}>{role.vocationalLens.fieldPrompt}</p>
                </div>
              </div>
            )}

            {activeTab === 'amanah' && (
              <div className="space-y-3">
                {role.mrgDomains.map((d, i) => (
                  <div key={i} className="rounded-lg p-4" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                    <p className="font-mono text-[10px] font-black uppercase tracking-widest mb-1" style={{ color: role.color }}>{d.domain}</p>
                    <p style={{ color: 'rgba(212,197,169,0.45)', fontSize: 11, marginBottom: 6, fontStyle: 'italic' }}>{d.trust}</p>
                    <p style={{ color: 'rgba(212,197,169,0.7)', fontSize: 12 }}>→ {d.action}</p>
                  </div>
                ))}
                <div className="rounded-xl p-5 mt-4" style={{ background: `${role.color}08`, border: `1px solid ${role.color}25` }}>
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: `${role.color}60` }}>Amanah Bridge</p>
                  <p style={{ color: 'rgba(212,197,169,0.75)', fontSize: 13, lineHeight: 1.7, fontStyle: 'italic' }}>"{role.amanahBridge}"</p>
                </div>
              </div>
            )}

            {activeTab === 'publish' && (
              <div>
                <div className="mb-4">
                  <p className="font-mono text-[9px] uppercase tracking-widest mb-2" style={{ color: `${role.color}60` }}>Publish a {role.label} Lens Contribution</p>
                  <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 12, lineHeight: 1.6 }}>
                    Contribute your vocational observation to the community role library. Your submission expands the open-ended role catalog for other caretakers.
                  </p>
                </div>

                {submitted ? (
                  <div className="rounded-xl p-6 text-center" style={{ background: `${role.color}10`, border: `1px solid ${role.color}30` }}>
                    <p className="font-mono text-sm font-black uppercase tracking-widest mb-2" style={{ color: role.color }}>Contribution Submitted</p>
                    <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 12 }}>Your {role.label} lens observation has been added to the community role library. Alhamdulilah.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {role.publishForm.fields.map(field => (
                      <div key={field}>
                        <label className="font-mono text-[9px] uppercase tracking-widest block mb-1" style={{ color: 'rgba(212,197,169,0.4)' }}>{field}</label>
                        <textarea
                          value={publishForm[field] || ''}
                          onChange={e => setPublishForm(prev => ({ ...prev, [field]: e.target.value }))}
                          rows={2}
                          placeholder={`Your ${field.toLowerCase()}...`}
                          className="w-full rounded-lg px-3 py-2 font-mono text-xs resize-none"
                          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(212,197,169,0.7)', outline: 'none' }}
                        />
                      </div>
                    ))}
                    <div className="flex gap-3 mt-4">
                      <button
                        onClick={handlePublishSubmit}
                        className="font-mono text-[10px] tracking-widest uppercase px-5 py-2.5 transition-all"
                        style={{ background: `${role.color}20`, color: role.color, border: `1px solid ${role.color}40` }}
                      >
                        Publish Contribution →
                      </button>
                      <button
                        className="font-mono text-[10px] tracking-widest uppercase px-5 py-2.5 transition-all"
                        style={{ background: 'transparent', color: 'rgba(212,197,169,0.4)', border: '1px solid rgba(255,255,255,0.08)' }}
                      >
                        Save as Draft
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FULL BRANCHING ONBOARDING SECTION ───────────────────────────────────────

export function BranchingOnboardingSection() {
  const [activeArchetype, setActiveArchetype] = useState<'engineer' | 'farmer' | 'returning-citizen'>('engineer');

  const archetypes = [
    { id: 'engineer', label: 'Engineer', color: '#2d8a87', icon: '⚙' },
    { id: 'farmer', label: 'Farmer', color: '#2d7a47', icon: '⬡' },
    { id: 'returning-citizen', label: 'Returning Citizen', color: '#5a5a8a', icon: '⌘' },
  ] as const;

  return (
    <div>
      <div className="mb-6">
        <p className="font-mono text-[9px] uppercase tracking-widest mb-3" style={{ color: 'rgba(139,105,20,0.6)' }}>
          Branching Onboarding Flows · Three Role Archetypes
        </p>
        <p style={{ color: 'rgba(212,197,169,0.55)', fontSize: 13, lineHeight: 1.7 }}>
          Each role archetype enters the platform through a different first surface. The engineer sees systems and provision first. The farmer sees soil and growth first. The returning citizen sees Mizan and restoration first. Each path surfaces role-specific Keys, sequences, MRG domains, and Amanah bridges.
        </p>
      </div>

      {/* Archetype selector */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {archetypes.map(a => (
          <button
            key={a.id}
            onClick={() => setActiveArchetype(a.id)}
            className="flex items-center gap-2 font-mono text-[10px] tracking-widest uppercase px-4 py-2.5 transition-all"
            style={{
              background: activeArchetype === a.id ? `${a.color}15` : 'transparent',
              border: `1px solid ${activeArchetype === a.id ? a.color + '50' : 'rgba(255,255,255,0.08)'}`,
              color: activeArchetype === a.id ? a.color : 'rgba(212,197,169,0.4)',
            }}
          >
            <span>{a.icon}</span>
            <span>{a.label}</span>
          </button>
        ))}
      </div>

      <BranchingOnboardingFlow roleId={activeArchetype} />
    </div>
  );
}

// ─── ROLE MODULES SECTION (full page section) ─────────────────────────────────

export default function RoleModulesSection() {
  return (
    <section id="section-role-modules" style={{ background: 'rgba(6,13,18,0.97)', borderTop: '1px solid rgba(201,168,76,0.08)', padding: '80px 0' }}>
      <div className="max-w-[1100px] mx-auto px-6 md:px-10">
        <div className="mb-12">
          <p className="font-mono text-[9px] tracking-widest uppercase mb-4" style={{ color: 'rgba(139,105,20,0.6)' }}>
            ◈ Role Modules · Six Seed Archetypes
          </p>
          <h2 className="font-mono text-3xl md:text-4xl font-black uppercase tracking-tight mb-4" style={{ color: '#F5F0E8' }}>
            What if we modeled all the possible roles?
          </h2>
          <p style={{ color: 'rgba(212,197,169,0.6)', fontSize: 14, lineHeight: 1.8, maxWidth: 700 }}>
            Consider an engineer, a teacher, a scientist, a student, a farmer, a native, a formerly incarcerated individual... What if we redeveloped and recognized those roles with the primary purpose being oriented toward the remembrance of Allah, Al-Qadir; The All-Powerful?
          </p>
        </div>

        {/* Branching Onboarding */}
        <div className="mb-16">
          <h3 className="font-mono text-lg font-black uppercase tracking-tight mb-6" style={{ color: '#F5F0E8' }}>
            Branching Onboarding Flows
          </h3>
          <BranchingOnboardingSection />
        </div>

        {/* Six Seed Role Grid */}
        <div>
          <h3 className="font-mono text-lg font-black uppercase tracking-tight mb-2" style={{ color: '#F5F0E8' }}>
            Six Seed Role Modules
          </h3>
          <p style={{ color: 'rgba(212,197,169,0.5)', fontSize: 13, marginBottom: 24 }}>
            Select a role to explore its vocational IRL Lens re-orientation, Amanah domains, and publish/remix form for community contributions.
          </p>
          <SixSeedRoleModuleGrid />
        </div>
      </div>
    </section>
  );
}
